import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { Translations } from '../types';

interface HeroProps {
  t: Translations;
  currentLanguage: 'en' | 'zh';
}

export const Hero: React.FC<HeroProps> = ({ t, currentLanguage }) => {
  return (
    <section className="hero" role="banner" id="hero">
      <div className="hero-background" aria-hidden="true">
        <div className="grid-pattern"></div>
        <div className="glow-effect"></div>
        
        {/* K-line Chart Background */}
        <div className="kline-background">
          <div className="kline-chart" role="img" aria-label="K线图背景动画">
            {Array.from({length: 60}).map((_, i) => (
              <div 
                key={i} 
                className="kline-bar" 
                style={{
                  height: `${Math.random() * 60 + 20}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Trading Data Stream */}
        <div className="trading-stream" role="img" aria-label="实时交易数据流">
          <div className="stream-item">
            <span>BTC/USDT</span>
            <span className="price-up">$67,234.56</span>
            <span className="price-up">+2.34%</span>
          </div>
          <div className="stream-item">
            <span>ETH/USDT</span>
            <span className="price-down">$3,456.78</span>
            <span className="price-down">-1.23%</span>
          </div>
          <div className="stream-item">
            <span>SOL/USDT</span>
            <span className="price-up">$234.56</span>
            <span className="price-up">+5.67%</span>
          </div>
          <div className="stream-item">
            <span>ALGO/USDT</span>
            <span className="price-up">$0.3456</span>
            <span className="price-up">+12.34%</span>
          </div>
        </div>

        {/* Matrix Rain Effect */}
        <div className="matrix-rain">
          <div className="matrix-column">
            {`0.1234\n0.5678\n0.9012\n0.3456\n0.7890\n0.2345\n0.6789\n0.0123`}
          </div>
          <div className="matrix-column">
            {`AI\nML\nQT\nBT\nDP\nNN\nRL\nDL`}
          </div>
          <div className="matrix-column">
            {`+23.4%\n-12.1%\n+5.67%\n+8.90%\n-3.21%\n+15.6%\n+7.89%\n-2.34%`}
          </div>
          <div className="matrix-column">
            {`67234\n45678\n23456\n78901\n34567\n89012\n56789\n01234`}
          </div>
          <div className="matrix-column">
            {`RSI:72\nMACD+\nBB:H\nSMA:↑\nEMA:↗\nVol:H\nATR:M\nFib:G`}
          </div>
          <div className="matrix-column">
            {`█▄▀▄\n▀█▄▀\n▄▀█▄\n▀▄▀█\n█▄▀▄\n▀█▄▀\n▄▀█▄\n▀▄▀█`}
          </div>
        </div>
      </div>
      
      <div className="hero-content">
        <motion.div
          className="hero-icon"
          initial={{ scale: 0, rotate: 180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          aria-hidden="true"
        >
          <TrendingUp size={80} />
        </motion.div>

        <motion.h1
          className="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {currentLanguage === 'en' ? (
            <>Your <span className="highlight-text">AI Quant Assistant</span> — Built for Traders</>
          ) : (
            <>您的<span className="highlight-text">AI量化助手</span> — 为交易者打造</>
          )}
        </motion.h1>

        <motion.div
          className="hero-brand"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {t.hero.subtitle}
        </motion.div>

        <motion.p
          className="hero-description"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {t.hero.description}
        </motion.p>

        <motion.div
          className="hero-cta"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="cta-buttons">
            <motion.button
              className="cta-button secondary"
              onClick={() => window.open('https://app.alphora-ai.com', '_blank')}
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 255, 255, 0.1)" }}
              whileTap={{ scale: 0.95 }}
              aria-label="View demo"
            >
              {t.hero.demoButton}
            </motion.button>
          </div>
          <p className="hero-limited">{t.hero.limited}</p>
        </motion.div>

        <motion.p
          className="hero-credibility"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          {t.hero.credibility}
        </motion.p>
      </div>
    </section>
  );
}; 
